import React, { useState, useEffect } from 'react';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';
import { CLIENT_FIELDS, clientToForm, clientPatch, parseSinceYear } from '../utils/clientPatch';
import { loadPrimaryContact, primaryToForm, primaryProblem, primaryChanged, savePrimaryContact } from '../utils/clientPrimaryContact';

export const CLIENT_TYPE_OPTIONS = ['A Level', 'B Level', 'C Level'];

// AddClientModal — THE client form: creates a client, or edits one when given `client`. Mounted from Onboarding
// (Add), the client page (Edit Client) and Admin Console → Client Management (Edit). One form, so the field list
// can't drift between create and edit.
//
// Props: isDark; client (null = create; a row or { id } = edit); onClose; onSuccess(clientRow).
//
// EDIT loads the row FRESH from the database when it opens (the caller's copy can be stale — the client page's
// comes from sessionStorage) and saves ONLY the fields that changed against it (src/utils/clientPatch.js): an
// untouched field is never overwritten, and a field emptied on purpose is cleared (NULL). The update checks its
// affected-row count — clients is RLS-enabled, and a blocked write returns 0 rows and no error.
//
// THE PRIMARY CONTACT IS THE CLIENT SCREEN'S (Natalie / Philip 2026-10-28, utils/clientPrimaryContact): the client_contacts
// person flagged Primary — read when the form opens, and saved to that same person — so an edit here shows on the client
// screen and the reverse. The old clients.primary_contact_* text columns are no longer written.
//
// ⛔ THERE IS A SECOND CLIENT INSERT, AND THIS IS NOT IT. AdminPanel's Bulk Import
// (handleBulkImport, `.from('clients').insert(...)`) is a separate implementation and was
// deliberately left untouched when this was extracted. They differ:
//
//   • billing_contact and contract_start_date — written here, NOT WRITTEN AT ALL by bulk import
//     (they are also absent from the CSV header list the bulk UI advertises, so a client created by
//     import can never carry them).
//   • null handling — bulk writes `row.x || null` (explicit NULL, since its CSV parser coerces
//     missing cells to ''); this form omits untouched keys entirely, because they are `undefined`
//     and JSON.stringify drops them, so the column takes its database default instead. Same result
//     for a nullable column with no default; different the moment one has a default.
//   • editing writes only CHANGED fields, and an emptied one as NULL (clientPatch) — a third convention, but
//     one that touches only fields the user actually changed.
//   • the primary contact — the same in both: created as the client screen's contact (savePrimaryContact); bulk
//     import splits its one primary_contact_name column into first / last name (splitName).
//
// Converging them was considered and deferred — it changes bulk-import behaviour and is only worth
// doing alongside a decision about extending the CSV headers. If you are here to change how a client
// is created, change BOTH or neither.
export default function AddClientModal({ isDark, client, onClose, onSuccess }) {
  const editing = !!client;
  const [original, setOriginal] = useState(null);     // edit: the row as loaded — what "changed" is measured against
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({
    name: '', short_name: '', client_type: '', client_since_year: '', billing_contact: '',
    contract_start_date: '', notes: '',
    // ⛔ THE FIVE STRUCTURED COLUMNS, NOT THE FLAT `address`. The client drill's address block
    // (StaffPortal.js:1856-1866) reads address_line1/2, city, state, zip and NOTHING reads
    // clients.address — so everything typed into the old single Address field was written and then
    // never shown again. Column is `zip` here; properties and consultants call theirs postal_code.
    address_line1: '', address_line2: '', city: '', state: '', zip: '',
  });
  const [primary, setPrimary] = useState(() => primaryToForm(null));   // the primary contact's fields
  const [origPrimary, setOrigPrimary] = useState(null);                // edit: the primary contact as loaded (null = none)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Edit: load the row fresh, by id — and its primary contact.
  useEffect(() => {
    if (!editing) return;
    let alive = true;
    (async () => {
      const { data, error: err } = await supabase.from('clients').select(['id', ...CLIENT_FIELDS].join(', ')).eq('id', client.id).maybeSingle();
      if (!alive) return;
      if (err || !data) { setLoadError(err ? err.message : 'This client could not be found (0 rows).'); return; }
      let contact;
      try { contact = await loadPrimaryContact(data.id); } catch (e) { if (alive) setLoadError(e.message || 'The primary contact could not be read.'); return; }
      if (!alive) return;
      setOriginal(data);
      setForm(clientToForm(data));
      setOrigPrimary(contact);
      setPrimary(primaryToForm(contact));
    })();
    return () => { alive = false; };
  }, [editing, client && client.id]);

  const c = isDark ? {
    card: '#2F3854', border: '#374060', text: '#e8eaf2',
    muted: 'var(--slate)', input: 'var(--ink)', inputBorder: '#434C68',
  } : {
    card: '#ffffff', border: 'var(--line)', text: 'var(--ink)',
    muted: 'var(--slate)', input: '#ffffff', inputBorder: '#c8ccd8',
  };

  const s = {
    label:    { display:'block', fontSize:'11px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' },
    input:    { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:"'Public Sans',system-ui,sans-serif" },
    textarea: { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:"'Public Sans',system-ui,sans-serif", minHeight:'58px', resize:'vertical' },
    select:   { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btn:      { padding:'9px 18px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'7px', color:'white', fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding:'9px 16px', background:'transparent', border:`1px solid ${c.border}`, borderRadius:'7px', color:c.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    grid:     { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'18px' },
  };

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));
  const setP = (field, val) => setPrimary(prev => ({ ...prev, [field]: val }));
  const pField = (label, name, type) => (
    <div key={`primary_${name}`}>
      <label style={s.label}>{label}</label>
      <input style={s.input} type={type || 'text'} value={primary[name]} onChange={e => setP(name, e.target.value)} />
    </div>
  );

  const field = (label, name, type, options, full) => (
    <div key={name} style={full ? { gridColumn:'1 / -1' } : undefined}>
      <label style={s.label}>{label}</label>
      {options ? (
        <select style={s.select} value={form[name]} onChange={e => set(name, e.target.value)}>
          <option value="">— Select —</option>
          {/* A stored value outside the list (e.g. old casing) is shown as itself, not as "— Select —". */}
          {form[name] && !options.includes(form[name]) && <option value={form[name]}>{form[name]}</option>}
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea style={s.textarea} value={form[name]} onChange={e => set(name, e.target.value)} />
      ) : (
        <input style={s.input} type={type || 'text'} value={form[name]} onChange={e => set(name, e.target.value)} />
      )}
    </div>
  );

  async function handleSave() {
    if (editing) return saveEdit();
    // Same guard as the inline form: name is the only required field, and it derives the id.
    if (!form.name) { setError('Client Name is required.'); return; }
    const since = parseSinceYear(form.client_since_year);
    if (since.error) { setError(since.error); return; }
    const pProblem = primaryProblem(primary);
    if (pProblem) { setError(pProblem); return; }
    setSaving(true);
    setError('');
    // ⛔ ID DERIVATION IS VERBATIM, including the 30-char truncation. Two clients whose names
    // slugify to the same first 30 characters collide on the primary key — the insert fails with a
    // duplicate-key error rather than silently merging. Unchanged by this extraction; shared with
    // bulk import, which truncates identically.
    const id = form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30);
    const { data, error: err } = await supabase.from('clients').insert({
      id,
      name: form.name,
      short_name: form.short_name,
      client_type: form.client_type || 'A Level',
      client_since_year: since.value,
      status: 'Active',
      billing_contact: form.billing_contact,
      contract_start_date: form.contract_start_date || null,
      address_line1: form.address_line1,
      address_line2: form.address_line2,
      city: form.city,
      state: form.state,
      zip: form.zip,
      notes: form.notes,
    }).select().single();
    if (err) { setSaving(false); setError(err.message); return; }
    // The client exists now; its primary contact is the client screen's record. A failure here is said out loud — the
    // client is kept (the form closes onto it), and the contact can be added on the client screen.
    const pErr = await savePrimaryContact(data.id, null, primary);
    setSaving(false);
    if (pErr) window.alert(`The client was created, but its primary contact was not saved — add it on the client screen.\n\n${pErr}`);
    onSuccess(data);
  }

  async function saveEdit() {
    if (!original) return;
    const { patch, error: invalid } = clientPatch(original, form);
    if (invalid) { setError(invalid); return; }
    const pProblem = primaryProblem(primary);
    if (pProblem) { setError(pProblem); return; }
    const contactChanged = primaryChanged(origPrimary, primary);
    if (!Object.keys(patch).length && !contactChanged) { setError('Nothing changed — there is nothing to save.'); return; }
    setSaving(true);
    setError('');
    let row = original;
    if (Object.keys(patch).length) {
      const { data, error: err } = await supabase.from('clients').update(patch).eq('id', original.id).select('*');
      if (err) { setSaving(false); setError(err.message); return; }
      if (!data || !data.length) { setSaving(false); setError('Not saved — the update affected 0 rows (blocked, or the client no longer exists).'); return; }
      row = data[0];
      setOriginal(row);   // saved: a retry after a contact failure doesn't resend it
    }
    if (contactChanged) {
      const pErr = await savePrimaryContact(original.id, origPrimary, primary);
      if (pErr) { setSaving(false); setError(Object.keys(patch).length ? `The client fields were saved; the primary contact was not — ${pErr}` : pErr); return; }
    }
    setSaving(false);
    onSuccess(row);
  }

  return (
    <div {...backdropProps(onClose)}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:c.card, borderRadius:'14px', padding:'28px', maxWidth:'640px', width:'100%', boxShadow:'0 8px 40px rgba(0,0,0,0.3)', maxHeight:'calc(100vh - 40px)', overflowY:'auto', fontFamily:"'Public Sans',system-ui,sans-serif" }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px' }}>
          <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'20px', fontWeight:'700', color:c.text }}>{editing ? 'Edit Client' : 'Add Client'}</div>
          <button onClick={onClose} type="button" style={{ background:'none', border:'none', color:c.muted, fontSize:'18px', cursor:'pointer', padding:'0 4px', lineHeight:1 }}>✕</button>
        </div>
        <div style={{ fontSize:'12px', color:c.muted, marginBottom:'16px' }}>
          {editing ? 'Only the fields you change are saved. Empty a field to clear it.' : 'Create a client. Properties and contacts can be added once it exists.'}
        </div>

        {editing && !original ? (
          <div style={{ fontSize:'13px', color: loadError ? '#f87171' : c.muted, marginBottom:'18px' }}>{loadError ? `⚠ ${loadError}` : 'Loading…'}</div>
        ) : (
        <div style={s.grid}>
          {field('Client Name *', 'name', 'text', null, true)}
          {field('Short Name', 'short_name')}
          {field('Client Type', 'client_type', null, CLIENT_TYPE_OPTIONS)}
          {field('Client Since (year)', 'client_since_year', 'text')}
          {/* The client screen's Primary Contact — the same person (utils/clientPrimaryContact). Empty all five for none. */}
          <div style={{ gridColumn:'1 / -1', fontSize:'11px', fontWeight:'700', color:c.text, textTransform:'uppercase', letterSpacing:'0.06em', marginTop:'4px' }}>
            Primary Contact <span style={{ fontWeight:'400', color:c.muted, textTransform:'none', letterSpacing:0 }}>— the same contact as on the client screen</span>
          </div>
          {pField('First Name', 'first_name')}
          {pField('Last Name', 'last_name')}
          {pField('Email', 'email', 'email')}
          {pField('Phone', 'phone')}
          {pField('Title', 'title')}
          {field('Billing Contact', 'billing_contact')}
          {field('Contract Start Date', 'contract_start_date', 'date')}
          {field('Address Line 1', 'address_line1', 'text', null, true)}
          {field('Address Line 2', 'address_line2', 'text', null, true)}
          {field('City', 'city')}
          {field('State', 'state')}
          {field('Zip', 'zip')}
          <div />
          {field('Notes', 'notes', 'textarea', null, true)}
        </div>
        )}

        {error && (
          <div style={{ padding:'10px 12px', margin:'0 0 14px', background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:'7px', fontSize:'12px', color:'#f87171' }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px' }}>
          <button type="button" onClick={onClose} disabled={saving} style={s.btnGhost}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving || (editing && !original)} style={{ ...s.btn, opacity: (saving || (editing && !original)) ? 0.6 : 1, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving...' : editing ? 'Save Changes' : '+ Add Client'}
          </button>
        </div>
      </div>
    </div>
  );
}
